import { GatewayQuestionnaire } from "@conformite/gateway";
import { useQuestionnaireDefinitionQuery } from "@src/data/questionnaire/hook/useQuestionnaireDefinitionQuery";
import { useQuestionnaireLatestInstanceQuery } from "@src/data/questionnaire/hook/useQuestionnaireLatestInstanceQuery";
import { useMemo } from "react";
import { computeVisibleQuestionIds } from "./useQuestionConditionEvaluator";

type Result = {
  isComplete: boolean;
  isLoading: boolean;
};

export function isInstanceComplete(
  definition: GatewayQuestionnaire.QuestionnaireDefinitionResponse,
  instance: GatewayQuestionnaire.QuestionnaireInstanceResponse | null
): boolean {
  // Remappe par label car les questions sont recréées à chaque save admin.
  const labelToCurrentId = new Map<string, string>();
  definition.questions.forEach((q) => {
    labelToCurrentId.set(q.label, q.id);
  });
  const answers: Record<string, string> = {};
  if (instance) {
    instance.answers
      .filter((a) => Boolean(a.value))
      .forEach((a) => {
        const currentId =
          labelToCurrentId.get(a.label) ?? a.questionDefinitionId;
        answers[currentId] = a.value;
      });
  }

  if (Object.keys(answers).length === 0) return false;

  const visibleIds = computeVisibleQuestionIds(definition.questions, answers);
  const requiredVisibleQuestions = definition.questions.filter(
    (q) => q.required && visibleIds.has(q.id) && q.label !== "Commentaire"
  );
  return requiredVisibleQuestions.every((q) => Boolean(answers[q.id]));
}

export function useIsQuestionnaireComplete(
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined,
  contextType: string,
  contextId: string
): Result {
  const { data: definition, isLoading: isLoadingDefinition } =
    useQuestionnaireDefinitionQuery(slug);
  const { data: latestInstance, isLoading: isLoadingInstance } =
    useQuestionnaireLatestInstanceQuery(contextType, contextId, slug);

  const isComplete = useMemo(() => {
    if (!slug) return true;
    if (!definition) return false;
    return isInstanceComplete(definition, latestInstance ?? null);
  }, [slug, definition, latestInstance]);

  return {
    isComplete,
    isLoading: Boolean(slug) && (isLoadingDefinition || isLoadingInstance),
  };
}
